import { useCallback, useMemo, useRef, useState } from "react";
import EmptyState from "./EmptyState";
import InlineTitleEditor from "./InlineTitleEditor";
import LoadingSkeleton from "./LoadingSkeleton";
import RowMenu, { type RowMenuAction } from "./RowMenu";
import {
  closeTask,
  createTask,
  deleteTask,
  getTasksToday,
  reopenTask,
  updateTask,
} from "../lib/api";
import { useApi } from "../hooks/useApi";
import { useOptimisticWrites } from "../hooks/usePendingWrites";
import { useToast } from "../hooks/useToast";
import { mergeAdded, retireSettled, withPatched, withoutHidden } from "../lib/pending";
import { calendarDay } from "../lib/day";
import { moveTargetLabel, moveTargets } from "../lib/taskDue";
import type { TaskItem, TasksToday } from "../types";

/**
 * Today's Todoist list, alongside whatever period the Goals page is showing.
 *
 * Deliberately not tied to a goal: a task belongs to a day, a goal belongs to a
 * period, and requiring a tag before a task counted for anything would just
 * mean untagged tasks counting for nothing.
 *
 * **Writes here are optimistic, and they have to be.** Todoist is a network
 * away: a tick used to freeze the whole panel for the couple of seconds it took
 * to write upstream and read the list back, which on a list you rattle through
 * ten items at a time is most of the interaction. The screen now moves on the
 * click and the request follows behind it.
 *
 * The comment this file used to carry was right about the danger — "optimistic
 * state is where 'I ticked it but it came back' bugs live" — so the reconcile
 * rule is deliberate rather than hopeful: a local edit is retired only once a
 * request that *left after it succeeded* has come back (`lib/pending.ts`), and
 * a write that fails puts the row back and says so out loud (`useToast`). What
 * is gone is the blocking, not the honesty.
 */
export default function TodoPanel({
  className,
  title = "Сьогодні",
  today: givenToday,
}: {
  className?: string;
  /** Card heading — «Сьогодні» beside the goals, «Задачі на сьогодні» on the home hero. */
  title?: string;
  /**
   * The server's own today (`summary.today`, Kyiv). The list is bucketed in
   * Kyiv, so a browser on another clock must not decide what «сьогодні» means
   * here; without it the panel falls back to the browser's calendar day.
   */
  today?: string;
}) {
  const tasks = useApi<TasksToday>(getTasksToday, [], { pollMs: 120_000 });
  const toast = useToast();
  const [draft, setDraft] = useState("");
  const input = useRef<HTMLInputElement>(null);
  const nextTempId = useRef(1);
  /** Id of the row whose title is open for rewriting, or null. */
  const [editing, setEditing] = useState<string | null>(null);
  // The *calendar* day, not `dayInProgress`: a Todoist due date is a date, and
  // at 01:00 «На завтра» has to mean the date tomorrow's calendar shows. The
  // page's own `today` wins when it has one — the list arrives bucketed in
  // Kyiv, and a laptop an hour behind would otherwise hide today's tasks as
  // «перенесено» and offer «На сьогодні» for the day they are already on.
  const today = givenToday ?? calendarDay();

  /** Local edits waiting for the server to agree. See `hooks/usePendingWrites`. */
  const { writes, run } = useOptimisticWrites<TaskEdit>(tasks.startedAt, tasks.refetch);

  const add = useCallback(
    (content: string) => {
      const key = `new-${nextTempId.current++}`;
      // A placeholder good enough to render; Todoist replaces it on settle.
      const task: TaskItem = {
        id: key,
        content,
        project: "Inbox",
        due_day: null,
        due_time: null,
        priority: 1,
        overdue_days: 0,
      };
      void run({
        key,
        edit: { kind: "add", task },
        send: () => createTask(content),
        failure: `Не вдалося додати «${content}».`,
        retry: () => add(content),
        // The row Todoist answers with carries the real id, which is how the
        // pending copy recognises itself in the next server list instead of
        // showing up twice.
        absorb: (created, edit) => ({ ...edit, task: created }),
      });
    },
    [run],
  );

  const reopen = useCallback(
    (task: TaskItem) => {
      void run({
        key: task.id,
        edit: { kind: "reopen", task },
        send: () => reopenTask(task.id),
        failure: `Не вдалося повернути «${task.content}».`,
        retry: () => reopen(task),
      });
    },
    [run],
  );

  const close = useCallback(
    (task: TaskItem) => {
      void run({
        key: task.id,
        edit: { kind: "close", task },
        send: () => closeTask(task.id),
        failure: `Не вдалося закрити «${task.content}».`,
        retry: () => close(task),
      });
      // Ticking is the one action here with no way back through the UI, and
      // the row is gone the instant it is clicked. `reopenTask` has existed
      // since this panel shipped and was wired to nothing until now.
      // One shared key, so rattling through five tasks leaves one offer of
      // undo for the last of them rather than a stack of five.
      toast({
        message: `Закрито «${task.content}»`,
        tone: "ok",
        key: "task:closed",
        action: { label: "Повернути", onSelect: () => reopen(task) },
      });
    },
    [run, toast, reopen],
  );

  const remove = useCallback(
    (task: TaskItem) => {
      void run({
        key: task.id,
        edit: { kind: "delete", task },
        send: () => deleteTask(task.id),
        failure: `Не вдалося видалити «${task.content}».`,
        retry: () => remove(task),
      });
    },
    [run],
  );

  // Edits keep their own key space (`edit:<id>`), so a rename still in the air
  // and a tick on the same row are two writes rather than one overwriting the
  // other — which is how a settling rename used to put a closed row back.
  const rename = useCallback(
    (task: TaskItem, content: string) => {
      void run({
        key: `edit:${task.id}`,
        edit: { kind: "update", task: { ...task, content } },
        send: () => updateTask(task.id, { content }),
        failure: `Не вдалося перейменувати «${task.content}».`,
        retry: () => rename(task, content),
        absorb: (updated, edit) => ({ ...edit, task: updated }),
      });
      // No success toast: the new title is already on the row, and a toast
      // saying so would be the screen congratulating itself.
    },
    [run],
  );

  /**
   * Push a task to another day, keeping the hour it was due at.
   *
   * `announce` is off for the undo — an offer to undo the undo is a loop, and
   * the row coming back is its own confirmation.
   */
  const move = useCallback(
    (task: TaskItem, day: string, announce = true) => {
      const from = task.due_day;
      void run({
        key: `edit:${task.id}`,
        edit: {
          kind: "update",
          // Only the undo can aim at a past day, and it aims at this task's own
          // one — so the lateness it had is the lateness it gets back.
          task: { ...task, due_day: day, overdue_days: day < today ? task.overdue_days : 0 },
        },
        send: () => updateTask(task.id, { due_day: day, due_time: task.due_time }),
        failure: `Не вдалося перенести «${task.content}».`,
        retry: () => move(task, day, announce),
        absorb: (updated, edit) => ({ ...edit, task: updated }),
      });
      if (!announce) return;
      toast({
        message: `Перенесено на ${moveTargetLabel(day, today)}`,
        tone: "ok",
        // One shared key, like the tick: pushing four tasks to tomorrow leaves
        // one offer of undo, not a stack of four.
        key: "task:moved",
        ...(from && { action: { label: "Повернути", onSelect: () => move(task, from, false) } }),
      });
    },
    [run, toast, today],
  );

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const content = draft.trim();
    if (!content) return;
    setDraft("");
    add(content);
    // The field is never disabled, so the cursor never leaves it — but a
    // submit from the button rather than the keyboard needs putting back.
    input.current?.focus();
  };

  /**
   * The «⋯» menu of one row: rewrite it, push it to another day, throw it out.
   *
   * «Перенести» only lists days the task is not already on (`moveTargets`), so
   * a task due today is never offered «На сьогодні» and the submenu is dropped
   * entirely on the rare day when every horizon lands where the task already is.
   */
  const rowActions = (task: TaskItem): RowMenuAction[] => {
    const targets = moveTargets(today, task.due_day);
    return [
      { label: "Редагувати", onSelect: () => setEditing(task.id) },
      ...(targets.length > 0
        ? [
            {
              label: "Перенести",
              children: targets.map((target) => ({
                label: target.label,
                onSelect: () => move(task, target.day),
              })),
            },
          ]
        : []),
      {
        label: "Видалити",
        confirm: "Точно видалити?",
        danger: true,
        // A hairline above the one action with no way back, the way the notes
        // menu already separates its own.
        divider: true,
        onSelect: () => remove(task),
      },
    ];
  };

  const data = tasks.data;

  const { list, landing, closingNow, closedList } = useMemo(() => {
    // Retire at render, not only in the effect behind it: an edit the current
    // data already contains must stop counting in the same frame that data
    // arrives, or the tick shows up twice for one paint.
    const live = retireSettled(writes, tasks.startedAt);
    // Renamed or moved rows, which stay where they are and read differently.
    const edited = live.flatMap((w) => (w.value.kind === "update" ? [w.value.task] : []));
    const hidden = new Set([
      ...live
        .filter((w) => w.value.kind === "close" || w.value.kind === "delete")
        .map((w) => w.key),
      // A task pushed past today has left this panel, the same as a ticked one.
      ...edited.filter((t) => (t.due_day ?? "") > today).map((t) => t.id),
    ]);
    const patched = new Map(edited.map((t) => [t.id, t]));
    const added = live.flatMap((w) => (w.value.kind === "add" ? [w.value.task] : []));
    // A reopened task goes straight back on screen: the server list it came
    // from was fetched while it was still closed.
    const restored = live.flatMap((w) => (w.value.kind === "reopen" ? [w.value.task] : []));
    const closing = live.flatMap((w) => (w.value.kind === "close" ? [w.value.task] : []));
    const closingIds = new Set(closing.map((t) => t.id));
    const reopeningIds = new Set(restored.map((t) => t.id));
    return {
      // Hide *after* merging, not before: a row ticked off while its own «add»
      // was still pending used to be dropped from the server list and then put
      // straight back by `mergeAdded`, so it sat there refusing to be closed.
      //
      // The predicate is what makes «Повернути» after a move instant: once a
      // refetch has absorbed the move, the server list no longer holds the row
      // the undo edits, so the edit has to bring it back itself. Anything the
      // same render is hiding stays hidden — an undo must not resurrect a task
      // that was ticked off while it was away.
      list: withPatched(
        withoutHidden(mergeAdded(data?.tasks ?? [], [...restored, ...added]), hidden),
        patched,
        (task) => !hidden.has(task.id) && (task.due_day ?? "") <= today,
      ),
      // Rows whose request has not answered yet: Todoist has not told us their
      // real id, so there is nothing to act on them with.
      landing: new Set(
        live
          .filter((w) => w.settledAt === null && w.value.kind === "add")
          .map((w) => w.value.task.id),
      ),
      // Keep «N закрито» honest while the writes are still in the air.
      closingNow: closing.length,
      // Just-ticked rows appear here immediately; a row mid-reopen drops out
      // before the server has confirmed it, same as the active list above.
      closedList: [
        ...closing,
        ...(data?.closed ?? []).filter((t) => !closingIds.has(t.id) && !reopeningIds.has(t.id)),
      ],
    };
  }, [data, writes, tasks.startedAt, today]);

  const completed = (data?.completed_today ?? 0) + closingNow;
  // «4 з 6» — closed out of everything today asked for. Both halves move
  // together on a tick, which is the whole point of showing them as a pair.
  const total = completed + list.length;

  return (
    <aside
      className={`jv-card flex flex-col gap-3 p-3 sm:p-4 ${className ?? ""}`}
      aria-label="Задачі на сьогодні"
    >
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-fg">{title}</h2>
        {/* No count until the first response: «0 з 0» over an empty panel
            reads as "no tasks" while Todoist is still answering. */}
        {data && (
          <span className="shrink-0 text-xs text-muted">
            <span className="jv-num">{completed}</span> з <span className="jv-num">{total}</span>
          </span>
        )}
      </div>

      {tasks.loading && !data && <LoadingSkeleton variant="lines" rows={4} />}

      {data && !data.available && (
        <EmptyState
          compact
          icon="⚠"
          title="Todoist недоступний"
          note={data.meta.note ?? undefined}
        />
      )}

      {/* Only when the day is genuinely blank. A list that is entirely ticked
          off still has rows on screen, and «порожньо» over them is a lie. */}
      {data?.available && list.length === 0 && closedList.length === 0 && (
        <EmptyState compact icon="✓" title="На сьогодні порожньо" />
      )}

      {(list.length > 0 || closedList.length > 0) && (
        <ul className="flex flex-col gap-0.5">
          {list.map((task) => {
            const inFlight = landing.has(task.id);
            const isEditing = editing === task.id;
            return (
              <li
                key={task.id}
                aria-busy={inFlight || undefined}
                className={`group flex items-center gap-2 rounded-control py-1 pl-1 transition-opacity ${
                  inFlight ? "opacity-50" : ""
                }`}
              >
                <input
                  type="checkbox"
                  checked={false}
                  // Only the row still waiting for its id is inert. Every other
                  // row stays live while this one is in the air — the old panel
                  // disabled all of them at once, which is what made adding a
                  // task feel like the list had gone away.
                  disabled={inFlight}
                  onChange={() => close(task)}
                  aria-label={`Закрити «${task.content}»`}
                  className="size-4 shrink-0 accent-[var(--color-accent)]"
                />
                {isEditing ? (
                  <InlineTitleEditor
                    initial={task.content}
                    label={`Назва задачі «${task.content}»`}
                    onDone={(content) => {
                      setEditing(null);
                      if (content !== null) rename(task, content);
                    }}
                  />
                ) : (
                  <>
                    {/* Double-click opens the same editor the menu does. Not
                        gated on a pointer type: a touch device simply never
                        fires it, and the menu is there for both. Inert while
                        the row lands, for the same reason as the checkbox —
                        Todoist has not issued the id a rename would go to. */}
                    <span
                      className="min-w-0 flex-1 text-sm break-words text-fg"
                      onDoubleClick={inFlight ? undefined : () => setEditing(task.id)}
                    >
                      {task.content}
                    </span>
                    <TaskMeta task={task} />
                    {/* Hidden rather than unmounted while the row lands: deleting
                        an id Todoist has not issued yet would 404, but dropping the
                        button would shift the row sideways as it settles. */}
                    <span className={`shrink-0 ${inFlight ? "invisible pointer-events-none" : ""}`}>
                      <RowMenu label={`Дії для «${task.content}»`} actions={rowActions(task)} />
                    </span>
                  </>
                )}
              </li>
            );
          })}

          {/* Done, and still on screen. The toggle that used to hide these is
              gone (Vitalik, 2026-09-07): what he ticked off today is half of
              what the panel is for, and a list that shortens as you work it
              reads as work disappearing rather than work finished. Dimmed and
              struck through, after the open rows, so the eye still lands on
              what is left first. */}
          {closedList.map((task) => (
            // `pr-10` keeps the text column identical to an open row, whose
            // last 40px belong to the «⋯» a closed row has no use for.
            <li key={task.id} className="flex items-center gap-2 rounded-control py-1 pr-10 pl-1">
              <input
                type="checkbox"
                checked
                onChange={() => reopen(task)}
                aria-label={`Повернути «${task.content}»`}
                className="size-4 shrink-0 accent-[var(--color-accent)]"
              />
              <span className="min-w-0 flex-1 text-sm break-words text-muted line-through">
                {task.content}
              </span>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={submit} className="flex gap-2">
        <input
          ref={input}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Нова задача…"
          aria-label="Нова задача"
          // Never disabled while a write is in flight: typing the next task
          // before the last one has landed is the normal way to use this.
          disabled={data?.available === false}
          className="jv-input min-w-0 flex-1"
        />
        <button type="submit" className="jv-btn shrink-0" disabled={!draft.trim()}>
          Додати
        </button>
      </form>

      {/* Failures split by what they cost. Nothing on screen yet — say so in
          full. A background refresh that failed over a list that is still
          there is a staleness notice, not an alarm; the writes that used to
          report here go to a toast instead, because by then the row has
          already moved. */}
      {tasks.error &&
        (data ? (
          <p className="text-micro text-muted">Список міг застаріти — не вдалось оновити.</p>
        ) : (
          <p className="text-xs text-danger" role="alert">
            {tasks.error}
          </p>
        ))}
    </aside>
  );
}

/**
 * The right-hand end of a row: when the task is due today, or how late it is.
 *
 * One slot, three states, in priority order — a Todoist time («09:00»), then
 * how many days it has been overdue, then nothing at all. They never stack:
 * a task with a time is not overdue, and a task with neither has nothing worth
 * a column of its own.
 */
function TaskMeta({ task }: { task: TaskItem }) {
  const at = task.due_time ?? null;
  if (at) return <span className="jv-num shrink-0 text-micro text-muted">{at}</span>;
  if (task.overdue_days > 0) {
    return (
      <span className="shrink-0 text-micro text-danger" title="Прострочено">
        +<span className="jv-num">{task.overdue_days}</span> дн.
      </span>
    );
  }
  return null;
}

/** What a pending write is doing, and the row it needs to render or restore. */
type TaskEdit =
  | { kind: "add"; task: TaskItem }
  | { kind: "close"; task: TaskItem }
  | { kind: "reopen"; task: TaskItem }
  | { kind: "delete"; task: TaskItem }
  /** A rename or a move: the row keeps its place and reads differently. */
  | { kind: "update"; task: TaskItem };
